import { Link } from "react-router-dom";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import { Check, ArrowUpRight } from "lucide-react";

const CONTENT = {
  owners: {
    overline: "For Owners",
    title: "Operational visibility,\nfinancial control.",
    blurb: "See what your captain sees — and what the accountant sees — without WhatsApp screenshots.",
    img: "https://images.unsplash.com/photo-1648997578921-39af26168d93",
    bullets: [
      "Live operational dashboard across the fleet",
      "Approve petty cash and big maintenance items",
      "Annual expense reports for tax & broker",
      "Resale-grade service history",
      "Insurance & document expiry alerts",
    ],
  },
  captains: {
    overline: "For Captains",
    title: "Run the daily operation\nin one app.",
    blurb: "Manage crew tasks, log every trip, file every service report and approve every receipt — from the bridge.",
    img: "https://images.unsplash.com/photo-1688223467306-64461bd749dd",
    bullets: [
      "Assign and audit deckhand checklists",
      "Recurring maintenance jobs (engine, gen, life raft)",
      "Trip & engine logbook with PDF export",
      "Approve crew expenses on the spot",
      "Service provider invites for one-off jobs",
    ],
  },
  crew: {
    overline: "For Crew",
    title: "Today's list,\nnot today's drama.",
    blurb: "Deckhands and engineers see exactly what they need to do — and tick it off with a photo.",
    img: "https://images.unsplash.com/photo-1612764324168-7a3a318e0cbb",
    bullets: [
      "Daily task list with priority and ETA",
      "Photo-on-completion for accountability",
      "Submit petty cash receipts in 10 seconds",
      "Update inventory counts without admin headaches",
      "Mobile-first — works on the dock",
    ],
  },
  providers: {
    overline: "For Service Providers",
    title: "Get hired,\nget paid, get rebooked.",
    blurb: "Marine engineers, electricians, riggers, sailmakers, surveyors and refit yards — work directly with captains and owners, with every job, photo and invoice in one place.",
    img: "https://images.unsplash.com/photo-1766431045548-80f04d8feef3",
    bullets: [
      "Free account — yachts pay the subscription, you don't",
      "Direct invites from captains and owners — no middlemen, no commission",
      "Every job becomes a verifiable service-history entry on the yacht — your work, on the record",
      "Send invoices in-app and watch approvals & payments move in real time",
      "Attach reports, photos and PDFs to each job — stop losing email threads",
      "One profile, many yachts — your business details (VAT, IBAN, address) sync automatically",
      "Mobile-first — quote and update jobs from the engine room",
    ],
  },
};

export default function RolePage({ role }) {
  const c = CONTENT[role] || CONTENT.owners;

  return (
    <div className="bg-white text-ink">
      <MarketingNav />

      <section className="py-20 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">{c.overline}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.05] font-medium whitespace-pre-line">
              {c.title}
            </h1>
            <p className="mt-6 text-zinc-600 max-w-md leading-relaxed">{c.blurb}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/request-access"
                data-testid="role-cta"
                className="inline-flex items-center gap-2 rounded-md bg-ink text-white px-5 py-3 text-sm font-medium hover:bg-black"
              >
                Request access <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/features" className="inline-flex items-center rounded-md border border-zinc-200 bg-white px-5 py-3 text-sm font-medium hover:bg-zinc-50">
                Browse features
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-zinc-200">
              <img src={c.img} alt={c.overline} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">What you get</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-5">
                <Check className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <span className="text-sm text-zinc-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
